import { apiClient } from './apiClient';
import { toast } from 'react-hot-toast';
import type { AuthActionResult, User, UserDetails } from '../types/auth';
import { captureHandledError } from '../utilities/datadog';

// Utility to generate a unique Device ID
// const getDeviceId = (): string => {
//   let deviceId = localStorage.getItem('device_id');
//   if (!deviceId) {
//     deviceId = crypto.randomUUID();
//     localStorage.setItem('device_id', deviceId);
//   }
//   return deviceId;
// };

// Fetch correct backend URL based on the environment

const login = async (username: string, password: string): Promise<boolean> => {
  try {
    const response = await apiClient(`/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      isErrorToastSuppressed: true,
    });

    if (response.ok) {
      return true; // Login was successful
    } else {
      const data = await response.json();
      const message =
        typeof data?.detail === 'string'
          ? data.detail
          : typeof data?.message === 'string'
            ? data.message
            : 'Authentication failed. Please try again.';
      throw new Error(message);
    }
  } catch (error) {
    captureHandledError('Login error', error);
    throw error;
  }
};

const register = async (userDetails: UserDetails): Promise<boolean> => {
  try {
    const response = await apiClient(`/auth/register`, {
      method: 'POST',
      body: JSON.stringify(userDetails),
    });

    if (response.ok) {
      return true;
    } else {
      const data = await response.json();
      captureHandledError('Registration failed', data);
      return false;
    }
  } catch (error) {
    captureHandledError('Registration error', error);
    throw error;
  }
};

const getCurrentUser = async (): Promise<User | null> => {
  try {
    const response = await apiClient(`/auth/me`, {
      method: 'GET',
      isErrorToastSuppressed: true,
    });

    if (response.ok) {
      const data: User = await response.json();
      return data;
    } else {
      const data = await response.json();
      captureHandledError('Failed to fetch current user', data);
      return null;
    }
  } catch (error) {
    captureHandledError('Error fetching current user', error);
    throw error;
  }
};

const submitSsoForm = (email: string): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/api/auth/sso';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'email';
  input.value = email;

  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

const ssoLogin = async (email: string): Promise<void> => {
  const body = new URLSearchParams({ email });
  const response = await apiClient('/auth/sso', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
    redirect: 'manual',
    isErrorHandlingSkipped: true,
  });

  const redirectLocation = response.headers.get('location');
  const isRedirectStatus = response.status >= 300 && response.status < 400;

  if (response.type === 'opaqueredirect' || isRedirectStatus) {
    if (redirectLocation) {
      window.location.assign(redirectLocation);
      return;
    }

    // Some browsers hide Location for cross-origin manual redirects.
    submitSsoForm(email);
    return;
  }

  if (response.redirected && response.url) {
    window.location.assign(response.url);
    return;
  }

  if (!response.ok) {
    const rawText = await response.text();
    let errorMessage = '';
    try {
      const parsedBody = JSON.parse(rawText);
      if (typeof parsedBody.detail === 'string') {
        errorMessage = parsedBody.detail;
      } else if (typeof parsedBody.message === 'string') {
        errorMessage = parsedBody.message;
      }
    } catch {
      // Ignore JSON parse errors; fallback to raw response text below.
    }
    throw new SsoLoginError(
      errorMessage || rawText || 'Unable to start SSO login.',
      response.status
    );
  }

  throw new Error('Unable to start SSO login.');
};

/** POST /auth/sso returns 404 when no Okta/SAML config matches the email's org/domain. */
class SsoLoginError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SsoLoginError';
    this.status = status;
  }
}

/** True when SSO initiate failed because no config exists (fall back to password). */
const isMissingSsoConfigError = (error: unknown): boolean =>
  error instanceof SsoLoginError && error.status === 404;

const logout = async (): Promise<void> => {
  try {
    const response = await apiClient('/auth/logout', {
      method: 'POST',
    });

    if (!response.ok) {
      captureHandledError(
        'Logout failed',
        new Error(response.statusText || 'logout failed')
      );
      toast.error('Failed to logout');
    }
  } catch (error) {
    captureHandledError('Error during logout', error);
  }
};

/** Extend JWT cookie expiry while the user remains active (sliding session). */
const refreshSession = async (): Promise<boolean> => {
  try {
    const response = await apiClient('/auth/refresh', {
      method: 'POST',
      isErrorToastSuppressed: true,
    });
    return response.ok;
  } catch (error) {
    captureHandledError('Session refresh error', error);
    return false;
  }
};

const acceptInvitation = async (
  token: string,
  password: string
): Promise<AuthActionResult> => {
  try {
    const response = await apiClient(`/auth/invite/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (response.ok) {
      const data = await response.json();
      return {
        isSuccess: true,
        message: data.msg || 'Invitation accepted successfully',
      };
    } else {
      const data = await response.json();
      return {
        isSuccess: false,
        message: data.detail || 'Failed to accept invitation',
      };
    }
  } catch (error) {
    captureHandledError('Accept invitation error', error);
    return { isSuccess: false, message: 'Failed to accept invitation' };
  }
};

/** Issues a session cookie as the given user. Superadmin + allowlist only. */
const loginAs = async (userId: string): Promise<AuthActionResult> => {
  try {
    const response = await apiClient('/auth/login-as', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
      isErrorToastSuppressed: true,
    });
    const data = await response.json();
    return {
      isSuccess: true,
      message:
        typeof data?.message === 'string' ? data.message : 'Login as successful',
    };
  } catch (error: unknown) {
    captureHandledError('Login as error', error);
    const msg =
      error instanceof Error && error.message.length > 0
        ? error.message
        : 'Failed to login as this user';
    return { isSuccess: false, message: msg };
  }
};

/** Triggers POST /api/auth/password/recover for the given email (same flow as forgot password). */
const requestPasswordRecovery = async (
  email: string
): Promise<AuthActionResult> => {
  try {
    const response = await apiClient(`/auth/password/recover`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      isErrorToastSuppressed: true,
    });
    const data = await response.json();
    const msg =
      typeof data.msg === 'string'
        ? data.msg
        : 'Password recovery email sent successfully';
    return { isSuccess: true, message: msg };
  } catch (error: unknown) {
    captureHandledError('Password recovery request error', error);
    const msg =
      error instanceof Error && error.message.length > 0
        ? error.message
        : 'Failed to send password recovery email';
    return { isSuccess: false, message: msg };
  }
};

const resetPassword = async (
  token: string,
  newPassword: string
): Promise<AuthActionResult> => {
  try {
    const response = await apiClient(`/auth/password/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (response.ok) {
      return { isSuccess: true, message: 'Password reset successful' };
    } else {
      const data = await response.json();
      return {
        isSuccess: false,
        message: data.detail || 'Password reset failed',
      };
    }
  } catch (error) {
    captureHandledError('Password reset error', error);
    return { isSuccess: false, message: 'Password reset error' };
  }
};

export {
  login,
  register,
  getCurrentUser,
  logout,
  refreshSession,
  ssoLogin,
  SsoLoginError,
  isMissingSsoConfigError,
  resetPassword,
  acceptInvitation,
  requestPasswordRecovery,
  loginAs,
};
